// bluaIQ — P3: Selection Board Module
// Visual board for finish selections with card layout, drag-and-drop, and filtering

import { apiFetch } from '../utils/api'
import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Palette, Filter, GripVertical, ChevronDown, ChevronUp, Check, Clock, ShoppingCart, Plus, Package, ExternalLink, Edit3, Trash2,
  Sparkles, X, Settings
} from 'lucide-react'
import { resolveSelectionPricing } from '../utils/selectionPricing'
import { TemplateLibraryModal, localizeTemplateName, localizeTemplateDescription } from './TemplateLibraryModal'
import { isDemoProject } from '../utils/demoData'
import SavedProjectOnlyNotice from './SavedProjectOnlyNotice'

// ── TYPES ─────────────────────────────────────────

type SelectionStatus = 'PENDING' | 'SELECTED' | 'APPROVED' | 'ORDERED'

interface ApiCategory {
  id: string
  name: string
  nameEs?: string
  allowance?: number
  description?: string
  items: ApiSelectionItem[]
}

interface SelectionOption {
  id: string
  selectionItemId: string
  name: string
  description?: string | null
  price: number
  imageUrl?: string | null
  externalUrl?: string | null
  supplierId?: string | null
  brand?: string | null
  modelNumber?: string | null
  isApprovedByClient: boolean
  approvedAt?: string | null
  approvedBy?: string | null
}

interface ApiSelectionItem {
  id: string
  categoryId: string
  name: string
  nameEs?: string
  status: SelectionStatus
  supplier?: string
  model?: string
  color?: string
  unitPrice?: number
  quantity?: number
  notes?: string
  notesEs?: string
  linkUrl?: string
  deadline?: string
  assignedTo?: string
  baseSpecification?: string
  baseUnitPrice?: number
  priceDelta?: number
  leadTimeDays?: number
  variationGroup?: string
  isComparableAlternative?: boolean
  requiresValidation?: boolean
  affectsTakeoff?: boolean
  options?: SelectionOption[]
}

interface SelectionItemDisplay extends ApiSelectionItem {
  categoryName: string
  categoryNameEs?: string
  categoryAllowance?: number | null
}

interface OptionFormData {
  name: string
  description: string
  price: number
  brand: string
  modelNumber: string
  imageUrl: string
  externalUrl: string
  supplierId: string
}

const EMPTY_OPTION_FORM: OptionFormData = {
  name: '', description: '', price: 0, brand: '', modelNumber: '',
  imageUrl: '', externalUrl: '', supplierId: '',
}

interface SelectionFormData {
  name: string
  categoryName: string
  status: SelectionStatus
  supplier: string
  model: string
  color: string
  unitPrice: number
  quantity: number
  notes: string
  linkUrl: string
  deadline: string
  assignedTo: string
  baseSpecification: string
  baseUnitPrice: number
  priceDelta: number
  leadTimeDays: number
  variationGroup: string
  isComparableAlternative: boolean
  requiresValidation: boolean
  affectsTakeoff: boolean
}

const EMPTY_FORM: SelectionFormData = {
  name: '', categoryName: '', status: 'PENDING', supplier: '', model: '',
  color: '', unitPrice: 0, quantity: 1, notes: '', linkUrl: '', deadline: '', assignedTo: '',
  baseSpecification: '', baseUnitPrice: 0, priceDelta: 0, leadTimeDays: 0, variationGroup: '',
  isComparableAlternative: false, requiresValidation: false, affectsTakeoff: false,
}

// ── DEMO DATA ─────────────────────────────────��───

const DEMO_SELECTIONS: SelectionItemDisplay[] = [
  { id: 'sel-1', categoryId: 'd', categoryName: 'Tile', name: 'Kitchen Backsplash Tile', nameEs: 'Azulejo Cocina', status: 'APPROVED', supplier: 'Floor & Decor', model: 'Carrara Marble Hex 2"', color: 'White/Gray', unitPrice: 12.50, quantity: 45, notes: 'Herringbone pattern', notesEs: 'Patrón espiga', linkUrl: 'https://www.flooranddecor.com' },
  { id: 'sel-2', categoryId: 'd', categoryName: 'Tile', name: 'Master Bath Floor Tile', nameEs: 'Piso Baño Principal', status: 'SELECTED', supplier: 'Floor & Decor', model: 'Porcelain Wood-Look 6x36', color: 'Light Oak', unitPrice: 4.80, quantity: 120, notes: 'Stagger 1/3 offset', notesEs: 'Colocación 1/3 desfasado' },
  { id: 'sel-3', categoryId: 'd', categoryName: 'Countertop', name: 'Kitchen Countertops', nameEs: 'Cubiertas Cocina', status: 'ORDERED', supplier: 'Arizona Tile', model: 'Quartz Calacatta', color: 'White Veined', unitPrice: 75.00, quantity: 42, notes: 'Eased edge, undermount sink cutout', notesEs: 'Borde recto, corte para tarja bajo cubierta' },
  { id: 'sel-4', categoryId: 'd', categoryName: 'Countertop', name: 'Master Bath Vanity Top', nameEs: 'Cubierta Tocador', status: 'PENDING', supplier: 'Arizona Tile', model: 'Marble Bianco', color: 'White', unitPrice: 85.00, quantity: 12, notes: 'Double sink cutout', notesEs: 'Corte doble lavabo' },
  { id: 'sel-5', categoryId: 'd', categoryName: 'Fixture', name: 'Kitchen Faucet', nameEs: 'Llave Cocina', status: 'APPROVED', supplier: 'Ferguson', model: 'Delta Trinsic Pull-Down', color: 'Matte Black', unitPrice: 380.00, quantity: 1, notes: 'Touch2O technology', notesEs: 'Tecnología Touch2O', linkUrl: 'https://www.homedepot.com' },
  { id: 'sel-6', categoryId: 'd', categoryName: 'Fixture', name: 'Master Bath Shower Fixture', nameEs: 'Regadera Baño Principal', status: 'SELECTED', supplier: 'Ferguson', model: 'Kohler Purist Rain', color: 'Brushed Nickel', unitPrice: 520.00, quantity: 1, notes: '12" rain head + handheld', notesEs: 'Cabeza 12" + de mano' },
  { id: 'sel-7', categoryId: 'd', categoryName: 'Paint', name: 'Interior Paint - Main', nameEs: 'Pintura Interior - Principal', status: 'APPROVED', supplier: 'Sherwin-Williams', model: 'SW 7015 Repose Gray', color: 'Warm Gray', unitPrice: 65.00, quantity: 12, notes: 'Eggshell finish, all common areas', notesEs: 'Acabado satinado, áreas comunes' },
  { id: 'sel-8', categoryId: 'd', categoryName: 'Paint', name: 'Accent Wall Paint', nameEs: 'Pintura Muro Acento', status: 'PENDING', supplier: 'Sherwin-Williams', model: 'SW 6119 Antique White', color: 'Cream', unitPrice: 65.00, quantity: 3, notes: 'Dining room feature wall', notesEs: 'Muro acento comedor' },
  { id: 'sel-9', categoryId: 'd', categoryName: 'Flooring', name: 'Living Room Flooring', nameEs: 'Piso Sala', status: 'ORDERED', supplier: 'Lumber Liquidators', model: 'Engineered Oak 7"', color: 'Natural White Oak', unitPrice: 8.50, quantity: 680, notes: 'Nail-down install on subfloor', notesEs: 'Instalación clavada sobre contrapiso', linkUrl: 'https://www.lowes.com' },
  { id: 'sel-10', categoryId: 'd', categoryName: 'Flooring', name: 'Guest Bath Flooring', nameEs: 'Piso Baño Visitas', status: 'PENDING', supplier: 'Floor & Decor', model: 'LVP Click 7x48', color: 'Aged Hickory', unitPrice: 3.20, quantity: 55, notes: 'Waterproof luxury vinyl', notesEs: 'Vinilo de lujo impermeable' },
  { id: 'sel-11', categoryId: 'd', categoryName: 'Exterior Finish', name: 'Front Elevation Siding Option', nameEs: 'Opción de Siding Fachada Frontal', status: 'SELECTED', supplier: 'Beacon', model: 'Hardie Plank 8.25 x 144', color: 'Arctic White', unitPrice: 11.75, baseSpecification: 'Fiber cement lap siding per plans', baseUnitPrice: 9.95, quantity: 140, priceDelta: 1.8, leadTimeDays: 14, variationGroup: 'exterior_finish', isComparableAlternative: true, requiresValidation: true, affectsTakeoff: true, notes: 'Comparable option for pricing; verify HOA + architect approval before order.', notesEs: 'Opción comparable para precio; verificar HOA + aprobación del arquitecto antes de ordenar.' },
]

const STATUS_CONFIG: Record<SelectionStatus, { label: string; labelEs: string; color: string; bg: string; icon: typeof Clock }> = {
  PENDING:  { label: 'Pending',  labelEs: 'Pendiente',  color: '#9E9890', bg: '#F2F0EB', icon: Clock },
  SELECTED: { label: 'Selected', labelEs: 'Seleccionado', color: '#06539B', bg: '#EEF2FF', icon: Check },
  APPROVED: { label: 'Approved', labelEs: 'Aprobado',   color: '#22C55E', bg: '#F0FDF4', icon: Check },
  ORDERED:  { label: 'Ordered',  labelEs: 'Ordenado',   color: '#F59E0B', bg: '#FFFBEB', icon: ShoppingCart },
}

const CATEGORY_PRESETS = [
  { en: 'Tile', es: 'Azulejo' },
  { en: 'Countertop', es: 'Encimera' },
  { en: 'Exterior Finish', es: 'Acabado Exterior' },
  { en: 'Fixture', es: 'Accesorio' },
  { en: 'Paint', es: 'Pintura' },
  { en: 'Flooring', es: 'Piso' },
  { en: 'Cabinets', es: 'Gabinetes' },
  { en: 'Appliances', es: 'Electrodomésticos' },
  { en: 'Hardware', es: 'Herrajes' },
  { en: 'Lighting', es: 'Iluminación' },
]

const VARIATION_GROUP_PRESETS = [
  { value: 'exterior_finish', en: 'Exterior finish', es: 'Acabado exterior' },
  { value: 'paint_palette', en: 'Paint palette', es: 'Paleta de pintura' },
  { value: 'flooring_package', en: 'Flooring package', es: 'Paquete de pisos' },
  { value: 'cabinet_package', en: 'Cabinet package', es: 'Paquete de gabinetes' },
  { value: 'appliance_package', en: 'Appliance package', es: 'Paquete de electrodomésticos' },
  { value: 'fixture_package', en: 'Fixture package', es: 'Paquete de accesorios' },
]

function getVariationGroupLabel(value: string | undefined, isEs: boolean) {
  if (!value) return null
  const preset = VARIATION_GROUP_PRESETS.find(group => group.value === value)
  return preset ? (isEs ? preset.es : preset.en) : value
}

// Strip empties so the backend Zod schema (.url() on imageUrl/externalUrl)
// doesn't reject blank optional fields. Sends only the keys with values.
function serializeOptionForm(form: OptionFormData) {
  return {
    name: form.name.trim(),
    price: Number(form.price) || 0,
    description: form.description.trim() || undefined,
    brand: form.brand.trim() || undefined,
    modelNumber: form.modelNumber.trim() || undefined,
    imageUrl: form.imageUrl.trim() || undefined,
    externalUrl: form.externalUrl.trim() || undefined,
    supplierId: form.supplierId.trim() || undefined,
  }
}

// Delta calc per spec: ámbar (#F59E0B) for upgrade, verde (#059669) for ahorro,
// gris (#9E9890) for igual. Strings come from i18n.
function buildDeltaPill(price: number, allowance: number | null | undefined, t: (k: string, opts?: Record<string, unknown>) => string) {
  if (allowance == null) return null
  const delta = price - allowance
  const amount = Math.abs(delta).toLocaleString(undefined, { maximumFractionDigits: 2 })
  if (delta > 0) return { label: t('selections.options.delta.upgrade', { amount }), color: '#F59E0B' }
  if (delta < 0) return { label: t('selections.options.delta.downgrade', { amount }), color: '#059669' }
  return { label: t('selections.options.delta.equal'), color: '#9E9890' }
}

// ── COMPONENT ──────────────────────────────��──────

export default function SelectionBoardModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const queryClient = useQueryClient()

  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<SelectionStatus | 'all'>('all')
  const [dragId, setDragId] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formData, setFormData] = useState<SelectionFormData>(EMPTY_FORM)
  const [toast, setToast] = useState<string | null>(null)

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  // ── Queries ──────────────────────────────────────
  const {
    data: apiCategories,
    isLoading: selectionsLoading,
    isError: selectionsError,
    refetch: refetchSelections,
  } = useQuery<ApiCategory[]>({
    queryKey: ['selections', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/selections`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
      })
      if (!res.ok) throw new Error('API unavailable')
      return res.json()
    },
    retry: false,
  })

  // Flatten categories + items into display items
  const apiItems: SelectionItemDisplay[] = (apiCategories || []).flatMap(cat =>
    cat.items.map(item => ({
      ...item,
      categoryName: cat.name,
      categoryNameEs: cat.nameEs,
      categoryAllowance: cat.allowance ?? null,
    }))
  )
  // PR #138B: rename the legacy `isDemo` (which actually meant
  // "we have no category data from the API") to clarify intent.
  // The new `isDemo` below is the canonical demo-project gate —
  // used to block all 8 mutation triggers in the JSX.
  const hasNoCategories = !apiCategories
  const isDemo = isDemoProject(projectId)

  // PR3 (beta-readiness): honest data source. A REAL project renders only its
  // own API items — never demo/fake fallback. Demo selections are shown solely
  // for explicitly-identified demo projects. When `apiCategories` is undefined
  // on a real project (loading or failed request) we resolve to an empty list,
  // and the loading / error guards before render show the truthful state.
  const activeSelections = apiCategories ? apiItems : (isDemo ? DEMO_SELECTIONS : [])

  const categoryNames = [...new Set(activeSelections.map(s => s.categoryName))]

  // Applying a template creates project selection CATEGORIES (with allowances),
  // not selection items — so a freshly-imported category has no cards yet and
  // would otherwise be invisible. Surface those empty categories as honest,
  // actionable scaffolding so "import" is visible (Visible = Functional).
  // Real (non-demo) projects only; never fabricated.
  const emptyCategories = (!isDemo && apiCategories)
    ? apiCategories.filter(c => (c.items?.length ?? 0) === 0)
    : []

  const filtered = useMemo(() => {
    return activeSelections.filter(s =>
      (filterCategory === 'all' || s.categoryName === filterCategory) &&
      (filterStatus === 'all' || s.status === filterStatus)
    )
  }, [activeSelections, filterCategory, filterStatus])

  // ── Mutations ──────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: async (data: SelectionFormData) => {
      const token = await getToken()
      const { categoryName, ...itemData } = data
      const computedPriceDelta = itemData.baseUnitPrice > 0 && itemData.unitPrice > 0
        ? itemData.unitPrice - itemData.baseUnitPrice
        : itemData.priceDelta || undefined
      const res = await apiFetch(`/api/projects/${projectId}/selection-items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          categoryName,
          ...itemData,
          unitPrice: itemData.unitPrice || undefined,
          quantity: itemData.quantity || undefined,
          deadline: itemData.deadline ? new Date(itemData.deadline + 'T12:00:00').toISOString() : undefined,
          assignedTo: itemData.assignedTo || undefined,
          linkUrl: itemData.linkUrl || undefined,
          baseSpecification: itemData.baseSpecification || undefined,
          baseUnitPrice: itemData.baseUnitPrice || undefined,
          priceDelta: computedPriceDelta,
          leadTimeDays: itemData.leadTimeDays || undefined,
          variationGroup: itemData.variationGroup || undefined,
        }),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'CREATE_FAILED')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['selections', projectId] })
      handleCloseForm()
      showToast(t('selections.toast.created'))
    },
    onError: (err: Error) => showToast(err.message),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<SelectionFormData> }) => {
      const token = await getToken()
      const computedPriceDelta = typeof data.baseUnitPrice === 'number' && data.baseUnitPrice > 0 && typeof data.unitPrice === 'number' && data.unitPrice > 0
        ? data.unitPrice - data.baseUnitPrice
        : data.priceDelta
      const serialized = {
        ...data,
        // Empty deadline must serialize to undefined (omit), NOT '' — the
        // backend schema is z.string().datetime() which REJECTS '' with a 400.
        // Create already did this; edit did not, so editing any selection
        // without a deadline failed → status/checkbox changes never persisted.
        deadline: data.deadline ? new Date(data.deadline + 'T12:00:00').toISOString() : undefined,
        baseSpecification: data.baseSpecification || undefined,
        baseUnitPrice: data.baseUnitPrice || undefined,
        priceDelta: computedPriceDelta || undefined,
        leadTimeDays: data.leadTimeDays || undefined,
        variationGroup: data.variationGroup || undefined,
      }
      const res = await apiFetch(`/api/selection-items/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify(serialized),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'UPDATE_FAILED')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['selections', projectId] })
      handleCloseForm()
      showToast(t('selections.toast.updated'))
    },
    onError: (err: Error) => showToast(err.message),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken()
      const res = await apiFetch(`/api/selection-items/${id}`, {
        method: 'DELETE',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('DELETE_FAILED')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['selections', projectId] })
      showToast(t('selections.toast.deleted'))
    },
  })

  // ── Options state + mutations (A.3) ────────────────
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false)
  const [optionModal, setOptionModal] = useState<{ itemId: string; editingId: string | null; form: OptionFormData } | null>(null)

  const invalidateSelections = () => queryClient.invalidateQueries({ queryKey: ['selections', projectId] })

  const optionCreate = useMutation({
    mutationFn: async ({ itemId, data }: { itemId: string; data: OptionFormData }) => {
      const token = await getToken()
      const res = await apiFetch(`/api/selections/items/${itemId}/options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify(serializeOptionForm(data)),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'CREATE_FAILED')
      return res.json()
    },
    onSuccess: () => { invalidateSelections(); setOptionModal(null) },
    onError: (err: Error) => showToast(err.message),
  })

  const optionUpdate = useMutation({
    mutationFn: async ({ optionId, data }: { optionId: string; data: OptionFormData }) => {
      const token = await getToken()
      const res = await apiFetch(`/api/selections/options/${optionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify(serializeOptionForm(data)),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'UPDATE_FAILED')
      return res.json()
    },
    onSuccess: () => { invalidateSelections(); setOptionModal(null) },
    onError: (err: Error) => showToast(err.message),
  })

  const optionDelete = useMutation({
    mutationFn: async (optionId: string) => {
      const token = await getToken()
      const res = await apiFetch(`/api/selections/options/${optionId}`, {
        method: 'DELETE',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('DELETE_FAILED')
      return res.json()
    },
    onSuccess: invalidateSelections,
  })

  const optionApprove = useMutation({
    mutationFn: async (optionId: string) => {
      const token = await getToken()
      const res = await apiFetch(`/api/selections/options/${optionId}/approve`, {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('APPROVE_FAILED')
      return res.json()
    },
    onSuccess: invalidateSelections,
  })

  const applyTemplate = useMutation({
    mutationFn: async (templateId: string) => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/selections/apply-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ templateId }),
      })
      if (!res.ok) throw new Error('APPLY_FAILED')
      return res.json()
    },
    onSuccess: (data: { categoriesCreated: number; categoriesSkipped?: number; totalInTemplate?: number }) => {
      invalidateSelections()
      setShowImportModal(false)
      const created = Number(data?.categoriesCreated) || 0
      const skipped = Number(data?.categoriesSkipped) || 0
      const total = Number(data?.totalInTemplate) || (created + skipped)
      // Honest feedback — apply is additive + de-duplicated, never silent:
      //   empty template → nothing to apply; all already existed → already applied;
      //   some added + some existed → partial; all added → success.
      if (total === 0) {
        showToast(isEs ? 'La plantilla no tenía categorías para aplicar.' : 'The template had no categories to apply.')
      } else if (created === 0) {
        showToast(t('selections.templates.import.alreadyApplied'))
      } else if (skipped > 0) {
        showToast(t('selections.templates.import.partialApplied', { added: created, skipped }))
      } else {
        showToast(t('selections.templates.import.successMessage', { count: created }))
      }
    },
    onError: (err: Error) => showToast(err.message),
  })

  // ── Handlers ──────────────────────────────────────

  const handleOpenForm = (item?: SelectionItemDisplay, presetCategory?: string) => {
    if (item) {
      setEditingId(item.id)
      setFormData({
        name: item.name,
        categoryName: item.categoryName,
        status: item.status,
        supplier: item.supplier || '',
        model: item.model || '',
        color: item.color || '',
        unitPrice: item.unitPrice || 0,
        quantity: item.quantity || 1,
        notes: item.notes || '',
        linkUrl: item.linkUrl || '',
        deadline: item.deadline ? item.deadline.slice(0, 10) : '',
        assignedTo: item.assignedTo || '',
        baseSpecification: item.baseSpecification || '',
        baseUnitPrice: item.baseUnitPrice || 0,
        priceDelta: item.priceDelta || 0,
        leadTimeDays: item.leadTimeDays || 0,
        variationGroup: item.variationGroup || '',
        isComparableAlternative: item.isComparableAlternative || false,
        requiresValidation: item.requiresValidation || false,
        affectsTakeoff: item.affectsTakeoff || false,
      })
    } else {
      setEditingId(null)
      setFormData({ ...EMPTY_FORM, categoryName: presetCategory || '' })
    }
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingId(null)
  }

  const handleSubmit = () => {
    // PR #138B: belt-and-suspenders gate.
    if (isDemo) return
    if (!formData.name.trim()) return
    if (!formData.categoryName) return
    if (editingId) {
      const { categoryName, ...data } = formData
      updateMutation.mutate({ id: editingId, data })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleDelete = (id: string) => {
    // PR #138B: belt-and-suspenders gate.
    if (isDemo) return
    if (window.confirm(t('selections.confirm.deleteMessage'))) {
      deleteMutation.mutate(id)
    }
  }

  // ── Drag and drop ──────────────────────────────
  const handleDragStart = (id: string) => setDragId(id)
  const handleDragOver = (e: React.DragEvent) => e.preventDefault()

  // ── KPIs ──────────────────────────────────────
  const totalValue = activeSelections.reduce((sum, s) => sum + (s.unitPrice || 0) * (s.quantity || 0), 0)
  const totalDeltaValue = activeSelections.reduce((sum, item) => {
    const pricing = resolveSelectionPricing(item)
    return sum + (pricing.totalDelta || 0)
  }, 0)
  const validationCount = activeSelections.filter(item => item.requiresValidation).length
  const statusCounts = activeSelections.reduce((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  // PR3 (beta-readiness): real projects get honest loading + error states —
  // never demo/fake fallback. Demo projects skip these guards and render the
  // demo board below. A successful-but-empty real project falls through to the
  // honest empty state inside the board.
  if (!isDemo && selectionsLoading) {
    return (
      <div className="p-5 max-w-6xl mx-auto" data-testid="selections-loading-state">
        <div className="bg-white border border-[#ECEAE4] rounded-2xl p-10 text-center">
          <div className="w-14 h-14 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Palette size={26} className="text-[#06539B]" />
          </div>
          <p className="text-[13px] font-[700] text-[#9E9890]">
            {isEs ? 'Cargando selecciones…' : 'Loading selections…'}
          </p>
        </div>
      </div>
    )
  }
  if (!isDemo && selectionsError) {
    return (
      <div className="p-5 max-w-6xl mx-auto" data-testid="selections-error-state">
        <div className="bg-white border border-dashed border-[#FECACA] rounded-2xl p-10 text-center">
          <div className="w-14 h-14 rounded-full bg-[#FEF2F2] flex items-center justify-center mx-auto mb-4">
            <Palette size={26} className="text-[#B91C1C]" />
          </div>
          <h3 className="text-[16px] font-[900] tracking-[-0.3px] text-[#1C1A17]">
            {isEs ? 'No se pudieron cargar las selecciones' : 'Couldn’t load selections'}
          </h3>
          <p className="text-[12px] font-[600] text-[#9E9890] mt-1.5 max-w-md mx-auto">
            {isEs
              ? 'No pudimos cargar las selecciones de este proyecto. Verifica tu conexión e inténtalo de nuevo.'
              : 'We couldn’t load this project’s selections. Check your connection and try again.'}
          </p>
          <button
            onClick={() => refetchSelections()}
            className="inline-flex items-center gap-1.5 mt-5 bg-[#06539B] hover:bg-[#14457E] text-white text-[13px] font-[800] px-5 py-2.5 rounded-full transition-colors"
          >
            {isEs ? 'Reintentar' : 'Try again'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-5 max-w-6xl mx-auto space-y-4 relative">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white text-[13px] font-[800] px-4 py-2 rounded-full shadow-lg">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17] flex items-center gap-2">
            <Palette size={18} />
            {t('selections.title')}
          </h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {activeSelections.length} {isEs ? (activeSelections.length === 1 ? 'selección' : 'selecciones') : (activeSelections.length === 1 ? 'selection' : 'selections')} · ${totalValue.toLocaleString()} total · {(totalDeltaValue >= 0 ? '+' : '-')}${Math.abs(totalDeltaValue).toLocaleString()} {isEs ? 'diferencia' : 'delta'}
          </p>
        </div>
        {/* PR #138B: in demo, the top action bar would open forms /
            templates that submit to selection/option/template endpoints
            against a non-existent project. Hide the bar entirely. */}
        {!isDemo && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-1.5 border border-[#ECEAE4] bg-white hover:border-[#06539B] hover:text-[#06539B] text-[#1C1A17] text-[13px] font-[800] px-4 py-2.5 rounded-full transition-colors"
            >
              <Sparkles size={14} /> {t('selections.templates.import.buttonLabel')}
            </button>
            <button
              onClick={() => handleOpenForm()}
              className="flex items-center gap-1.5 bg-[#06539B] hover:bg-[#14457E] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full transition-colors"
            >
              <Plus size={14} /> {t('selections.add')}
            </button>
            <button
              onClick={() => setShowTemplateLibrary(true)}
              className="flex items-center gap-1.5 border border-[#ECEAE4] rounded-full px-3 py-2 text-[11px] font-[800] text-[#1C1A17] hover:bg-[#FAFAF8]"
            >
              <Settings size={14} />
              {t('selections.board.manageTemplates')}
            </button>
          </div>
        )}
      </div>

      {/* PR #138B: demo banner. Demo selections list still renders below. */}
      {isDemo && (
        <SavedProjectOnlyNotice
          testId="selections-actions-demo-notice"
          bodyEn="Adding, editing, importing templates, and approving selections are available in saved projects. Create or open a real project to manage selections."
          bodyEs="Agregar, editar, importar plantillas y aprobar selecciones están disponibles en proyectos guardados. Crea o abre un proyecto real para gestionar selecciones."
        />
      )}

      <div className="rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] px-4 py-3">
        <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#06539B]">
          {isEs ? 'Uso recomendado' : 'Recommended use'}
        </div>
        <p className="mt-1 text-[12px] font-[600] text-[#4B5563]">
          {isEs
            ? 'Las variaciones de acabados exteriores, colores y comparaciones de proveedor deben vivir aquí en Selecciones, no dentro del takeoff técnico.'
            : 'Exterior finish variations, color choices, and supplier comparisons should live here in Selections, not inside the technical takeoff.'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-xl border border-[#ECEAE4] bg-white px-4 py-3">
          <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890]">
            {t('selections.summary.selectionDelta')}
          </div>
          <div className={`mt-1 text-[18px] font-[900] ${totalDeltaValue >= 0 ? 'text-[#065F46]' : 'text-[#B91C1C]'}`}>
            {totalDeltaValue >= 0 ? '+' : '-'}${Math.abs(totalDeltaValue).toLocaleString()}
          </div>
        </div>
        <div className="rounded-xl border border-[#ECEAE4] bg-white px-4 py-3">
          <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890]">
            {t('selections.summary.needsValidation')}
          </div>
          <div className="mt-1 text-[18px] font-[900] text-[#06539B]">
            {validationCount}
          </div>
        </div>
        <div className="rounded-xl border border-[#ECEAE4] bg-white px-4 py-3">
          <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#9E9890]">
            {t('selections.summary.exteriorVariants')}
          </div>
          <div className="mt-1 text-[18px] font-[900] text-[#1C1A17]">
            {activeSelections.filter(item => item.variationGroup === 'exterior_finish' || item.categoryName === 'Exterior Finish').length}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-3">
        {(['PENDING', 'SELECTED', 'APPROVED', 'ORDERED'] as SelectionStatus[]).map(status => {
          const cfg = STATUS_CONFIG[status]
          return (
            <div key={status} className="bg-white border border-[#ECEAE4] rounded-xl p-3">
              <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">
                {isEs ? cfg.labelEs : cfg.label}
              </div>
              <div className="text-[18px] font-[900]" style={{ color: cfg.color }}>
                {statusCounts[status] || 0}
              </div>
            </div>
          )
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 text-[12px] font-[700] text-[#9E9890]">
          <Filter size={13} /> {isEs ? 'Filtrar:' : 'Filter:'}
        </div>
        <select
          value={filterCategory}
          onChange={e => setFilterCategory(e.target.value)}
          className="text-[10px] font-[700] border border-[#ECEAE4] rounded-lg px-3 py-1.5 bg-white text-[#1C1A17]"
        >
          <option value="all">{t('selections.fields.allCategories')}</option>
          {categoryNames.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value as SelectionStatus | 'all')}
          className="text-[10px] font-[700] border border-[#ECEAE4] rounded-lg px-3 py-1.5 bg-white text-[#1C1A17]"
        >
          <option value="all">{t('selections.fields.allStatuses')}</option>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <option key={key} value={key}>{isEs ? cfg.labelEs : cfg.label}</option>
          ))}
        </select>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
          <h3 className="text-[13px] font-[800]">
            {editingId ? t('selections.editTitle') : t('selections.createFirst')}
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder={t('selections.fields.namePlaceholder')}
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
            <select
              value={formData.categoryName}
              onChange={e => setFormData({ ...formData, categoryName: e.target.value })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            >
              <option value="">{t('selections.fields.selectCategory')}</option>
              {/* Preserve a preset/custom category (e.g. from an applied
                  template) that isn't one of the built-in presets, so it
                  stays selected when adding a selection from the scaffold. */}
              {formData.categoryName && !CATEGORY_PRESETS.some(c => c.en === formData.categoryName) && (
                <option value={formData.categoryName}>{formData.categoryName}</option>
              )}
              {CATEGORY_PRESETS.map(c => (
                <option key={c.en} value={c.en}>{isEs ? c.es : c.en}</option>
              ))}
            </select>
          </div>
          {/* Selection status — Pending → Selected → Approved → Ordered.
              Changing it here PATCHes the item; the card badge + KPI counts
              update on the forced refetch. Makes the status KPIs functional. */}
          <label className="block">
            <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">
              {isEs ? 'Estado' : 'Status'}
            </div>
            <select
              value={formData.status}
              onChange={e => setFormData({ ...formData, status: e.target.value as SelectionStatus })}
              className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
              data-testid="selection-status-select"
            >
              {(['PENDING', 'SELECTED', 'APPROVED', 'ORDERED'] as SelectionStatus[]).map(s => (
                <option key={s} value={s}>{isEs ? STATUS_CONFIG[s].labelEs : STATUS_CONFIG[s].label}</option>
              ))}
            </select>
          </label>
          <input
            type="text"
            placeholder={t('selections.fields.baseSpecification')}
            value={formData.baseSpecification}
            onChange={e => setFormData({ ...formData, baseSpecification: e.target.value })}
            className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
          />
          <div className="grid grid-cols-3 gap-2">
            <input
              placeholder={t('selections.fields.supplier')}
              value={formData.supplier}
              onChange={e => setFormData({ ...formData, supplier: e.target.value })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
            <input
              placeholder={t('selections.fields.model')}
              value={formData.model}
              onChange={e => setFormData({ ...formData, model: e.target.value })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
            <input
              placeholder={t('selections.fields.color')}
              value={formData.color}
              onChange={e => setFormData({ ...formData, color: e.target.value })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
            <input
              type="number"
              placeholder={t('selections.fields.baseUnitPrice')}
              value={formData.baseUnitPrice || ''}
              onChange={e => setFormData({ ...formData, baseUnitPrice: parseFloat(e.target.value) || 0 })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
            <input
              type="number"
              placeholder={t('selections.fields.unitPrice')}
              value={formData.unitPrice || ''}
              onChange={e => setFormData({ ...formData, unitPrice: parseFloat(e.target.value) || 0 })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
            <input
              type="number"
              placeholder={t('selections.fields.quantity')}
              value={formData.quantity || ''}
              onChange={e => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
            <input
              type="number"
              placeholder={t('selections.fields.leadTimeDays')}
              value={formData.leadTimeDays || ''}
              onChange={e => setFormData({ ...formData, leadTimeDays: parseFloat(e.target.value) || 0 })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
            <input
              type="date"
              value={formData.deadline}
              onChange={e => setFormData({ ...formData, deadline: e.target.value })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
              title={t('selections.fields.deadline')}
            />
            <input
              placeholder={t('selections.fields.assignedToPlaceholder')}
              value={formData.assignedTo}
              onChange={e => setFormData({ ...formData, assignedTo: e.target.value })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={formData.variationGroup}
              onChange={e => setFormData({ ...formData, variationGroup: e.target.value })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            >
              <option value="">{t('selections.fields.variationGroup')}</option>
              {VARIATION_GROUP_PRESETS.map(group => (
                <option key={group.value} value={group.value}>{isEs ? group.es : group.en}</option>
              ))}
            </select>
            <input
              type="number"
              placeholder={t('selections.fields.priceDelta')}
              value={
                formData.baseUnitPrice > 0 && formData.unitPrice > 0
                  ? (formData.unitPrice - formData.baseUnitPrice).toFixed(2)
                  : (formData.priceDelta || '')
              }
              onChange={e => setFormData({ ...formData, priceDelta: parseFloat(e.target.value) || 0 })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <textarea
              placeholder={t('selections.fields.notesPlaceholder')}
              value={formData.notes}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
            <input
              placeholder={t('selections.fields.linkUrl')}
              value={formData.linkUrl}
              onChange={e => setFormData({ ...formData, linkUrl: e.target.value })}
              className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <label className="flex items-center gap-2 border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[700] text-[#1C1A17]">
              <input
                type="checkbox"
                checked={formData.isComparableAlternative}
                onChange={e => setFormData({ ...formData, isComparableAlternative: e.target.checked })}
              />
              {t('selections.fields.isComparableAlternative')}
            </label>
            <label className="flex items-center gap-2 border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[700] text-[#1C1A17]">
              <input
                type="checkbox"
                checked={formData.requiresValidation}
                onChange={e => setFormData({ ...formData, requiresValidation: e.target.checked })}
              />
              {t('selections.fields.requiresValidation')}
            </label>
            <label className="flex items-center gap-2 border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[700] text-[#1C1A17]">
              <input
                type="checkbox"
                checked={formData.affectsTakeoff}
                onChange={e => setFormData({ ...formData, affectsTakeoff: e.target.checked })}
              />
              {t('selections.fields.affectsTakeoff')}
            </label>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              disabled={!formData.name.trim() || !formData.categoryName || createMutation.isPending || updateMutation.isPending}
              className="bg-[#06539B] hover:bg-[#14457E] text-white rounded-full py-2 px-4 text-[11px] font-[800] disabled:opacity-40 transition-colors"
            >
              {(createMutation.isPending || updateMutation.isPending) ? '...' : editingId ? t('Save') : t('selections.createFirst')}
            </button>
            <button
              onClick={handleCloseForm}
              className="border border-[#ECEAE4] rounded-full py-2 px-4 text-[11px] font-[800] text-[#9E9890]"
            >
              {t('Cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Empty state — suppressed when there are imported/empty categories to
          act on (the scaffolding section below is the actionable surface). */}
      {activeSelections.length === 0 && emptyCategories.length === 0 && !showForm && (
        <div className="bg-white border border-dashed border-[#CFD8E3] rounded-2xl p-10 text-center">
          <div className="w-14 h-14 rounded-full bg-[#EEF2FF] flex items-center justify-center mx-auto mb-4">
            <Palette size={26} className="text-[#06539B]" />
          </div>
          <h3 className="text-[16px] font-[900] tracking-[-0.3px] text-[#1C1A17]">
            {t('selections.title')}
          </h3>
          <p className="text-[12px] font-[600] text-[#9E9890] mt-1.5 max-w-md mx-auto">
            {t('selections.empty')}
          </p>
          {/* PR #138B: empty-state create button only on real projects. */}
          {!isDemo && (
            <button
              onClick={() => handleOpenForm()}
              className="inline-flex items-center gap-1.5 mt-5 bg-[#06539B] hover:bg-[#14457E] text-white text-[13px] font-[800] px-5 py-2.5 rounded-full transition-colors"
            >
              <Plus size={14} /> {t('selections.createFirst')}
            </button>
          )}
        </div>
      )}

      {/* Imported / empty category scaffolding — makes "Importar plantilla"
          visible and actionable: applied categories appear here (name + base
          allowance) with a direct "Add selection" CTA, even before any
          selection cards exist. Real (non-demo) projects only; never fake. */}
      {emptyCategories.length > 0 && !showForm && (
        <div className="space-y-2" data-testid="selections-category-scaffold">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <h3 className="text-[12px] font-[900] uppercase tracking-[0.4px] text-[#1C1A17]">
              {isEs ? 'Categorías sin selecciones' : 'Categories awaiting selections'}
            </h3>
            <span className="text-[10px] font-[700] text-[#9E9890]">
              {isEs ? 'Agrega una selección a cada categoría de la plantilla.' : 'Add a selection to each template category.'}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {emptyCategories.map(cat => (
              <div key={cat.id} className="bg-white border border-dashed border-[#CFD8E3] rounded-xl px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[12px] font-[800] text-[#1C1A17] truncate">
                    {isEs ? (cat.nameEs || cat.name) : cat.name}
                  </div>
                  {cat.allowance != null && (
                    <div className="text-[10px] font-[700] text-[#06539B] mt-0.5">
                      {isEs ? 'Presupuesto base' : 'Base allowance'}: ${Number(cat.allowance).toLocaleString()}
                    </div>
                  )}
                </div>
                {!isDemo && (
                  <button
                    onClick={() => handleOpenForm(undefined, cat.name)}
                    className="flex items-center gap-1 bg-[#06539B] hover:bg-[#14457E] text-white text-[11px] font-[800] px-3 py-1.5 rounded-full whitespace-nowrap transition-colors"
                  >
                    <Plus size={12} /> {isEs ? 'Agregar selección' : 'Add selection'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selection cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(item => {
          const statusCfg = STATUS_CONFIG[item.status]
          const pricing = resolveSelectionPricing(item)
          const total = pricing.selectedTotal || 0
          return (
            <div
              key={item.id}
              draggable={!isDemo && !hasNoCategories}
              onDragStart={() => handleDragStart(item.id)}
              onDragOver={handleDragOver}
              className={`bg-white border border-[#ECEAE4] rounded-xl overflow-hidden transition-shadow hover:shadow-md ${
                dragId === item.id ? 'opacity-50' : ''
              }`}
            >
              {/* Image placeholder */}
              <div className="h-28 bg-[#F2F0EB] flex items-center justify-center relative">
                <Package size={28} className="text-[#CECAC4]" />
                <div className="absolute top-2 left-2">
                  <span
                    className="text-[8px] font-[800] uppercase tracking-[0.5px] px-2 py-0.5 rounded-full"
                    style={{ background: statusCfg.bg, color: statusCfg.color }}
                  >
                    {isEs ? statusCfg.labelEs : statusCfg.label}
                  </span>
                </div>
                <div className="absolute top-2 right-2 flex items-center gap-1">
                  {/* PR #138B: hide per-card edit/delete in demo OR while
                      apiCategories is still loading (preserves prior
                      hasNoCategories behavior). */}
                  {!isDemo && !hasNoCategories && (
                    <>
                      <button
                        onClick={() => handleOpenForm(item)}
                        className="p-1 rounded bg-white/80 hover:bg-white transition-colors"
                        title={t('selections.edit')}
                      >
                        <Edit3 size={12} className="text-[#9E9890] hover:text-[#06539B]" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-1 rounded bg-white/80 hover:bg-white transition-colors"
                        title={t('selections.delete')}
                      >
                        <Trash2 size={12} className="text-[#9E9890] hover:text-red-500" />
                      </button>
                    </>
                  )}
                  <GripVertical size={14} className="text-[#CECAC4] cursor-grab" />
                </div>
              </div>

              {/* Card body */}
              <div className="p-3 space-y-2">
                <div>
                  <div className="text-[11px] font-[800] text-[#1C1A17]">{isEs ? (item.nameEs || item.name) : item.name}</div>
                  <div className="text-[9px] font-[700] text-[#9E9890] mt-0.5">
                    {item.categoryName} {item.supplier ? `· ${item.supplier}` : ''}
                  </div>
                </div>

                {item.baseSpecification && (
                  <div className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] px-2.5 py-2">
                    <div className="text-[9px] font-[800] uppercase tracking-[0.4px] text-[#64748B]">
                      {t('selections.fields.baseSpecification')}
                    </div>
                    <div className="mt-0.5 text-[10px] font-[700] text-[#1C1A17]">
                      {item.baseSpecification}
                    </div>
                  </div>
                )}

                {(item.model || item.color || item.unitPrice != null || item.baseUnitPrice != null || item.quantity != null || item.leadTimeDays != null) && (
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-[600]">
                    {item.model && (
                      <div>
                        <span className="text-[#9E9890]">{isEs ? 'Modelo' : 'Model'}:</span>
                        <div className="font-[700] text-[#1C1A17] truncate">{item.model}</div>
                      </div>
                    )}
                    {item.color && (
                      <div>
                        <span className="text-[#9E9890]">{isEs ? 'Color' : 'Color'}:</span>
                        <div className="font-[700] text-[#1C1A17]">{item.color}</div>
                      </div>
                    )}
                    {item.unitPrice != null && (
                      <div>
                        <span className="text-[#9E9890]">{isEs ? 'Precio unitario seleccionado' : 'Selected unit price'}:</span>
                        <div className="font-[700] text-[#1C1A17]">${item.unitPrice.toLocaleString()}</div>
                      </div>
                    )}
                    {item.baseUnitPrice != null && (
                      <div>
                        <span className="text-[#9E9890]">{t('selections.fields.baseUnitPrice')}:</span>
                        <div className="font-[700] text-[#1C1A17]">${item.baseUnitPrice.toLocaleString()}</div>
                      </div>
                    )}
                    {item.quantity != null && (
                      <div>
                        <span className="text-[#9E9890]">{isEs ? 'Cantidad' : 'Qty'}:</span>
                        <div className="font-[700] text-[#1C1A17]">{item.quantity}</div>
                      </div>
                    )}
                    {item.leadTimeDays != null && (
                      <div>
                        <span className="text-[#9E9890]">{t('selections.fields.leadTimeDays')}:</span>
                        <div className="font-[700] text-[#1C1A17]">{item.leadTimeDays} {isEs ? 'días' : 'days'}</div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-1.5">
                  {item.variationGroup && (
                    <span className="text-[9px] font-[800] px-2 py-1 rounded-full bg-[#F1F5F9] text-[#334155]">
                      {getVariationGroupLabel(item.variationGroup, isEs)}
                    </span>
                  )}
                  {item.isComparableAlternative && (
                    <span className="text-[9px] font-[800] px-2 py-1 rounded-full bg-[#ECFDF5] text-[#047857]">
                      {t('selections.badges.comparable')}
                    </span>
                  )}
                  {item.requiresValidation && (
                    <span className="text-[9px] font-[800] px-2 py-1 rounded-full bg-[#FFF7ED] text-[#C2410C]">
                      {t('selections.badges.verify')}
                    </span>
                  )}
                  {item.affectsTakeoff && (
                    <span className="text-[9px] font-[800] px-2 py-1 rounded-full bg-[#EEF2FF] text-[#4338CA]">
                      {t('selections.badges.affectsTakeoff')}
                    </span>
                  )}
                </div>

                {total > 0 && (
                  <div className="flex items-center justify-between pt-1 border-t border-[#F2F0EB]">
                    <div className="text-[12px] font-[900] text-[#1C1A17]">${total.toLocaleString()}</div>
                    <span
                      className="text-[9px] font-[800] px-2.5 py-1 rounded-full"
                      style={{ background: statusCfg.bg, color: statusCfg.color }}
                    >
                      {isEs ? statusCfg.labelEs : statusCfg.label}
                    </span>
                  </div>
                )}

                {pricing.totalDelta !== null && (
                  <div className={`text-[10px] font-[800] ${pricing.totalDelta >= 0 ? 'text-[#065F46]' : 'text-[#B91C1C]'}`}>
                    {t('selections.fields.priceDelta')}: {pricing.totalDelta >= 0 ? '+' : '-'}${Math.abs(pricing.totalDelta).toLocaleString()}
                  </div>
                )}

                {(item.notes || item.notesEs) && (
                  <div className="text-[9px] font-[600] text-[#9E9890] italic">
                    {isEs ? (item.notesEs || item.notes) : item.notes}
                  </div>
                )}

                {item.linkUrl && (
                  <a
                    href={item.linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 w-full border border-[#ECEAE4] rounded-full py-1.5 text-[10px] font-[800] text-[#06539B] hover:bg-[#EEF2FF] transition-colors"
                  >
                    <ExternalLink size={11} /> {isEs ? 'Ver en Tienda' : 'View in Store'}
                  </a>
                )}

                {/* ── Options panel (A.3) ──────────────────
                    PR #138B: options panel performs real backend mutations
                    (create/update/delete/approve). Hide in demo and during
                    loading-no-data. */}
                {!isDemo && !hasNoCategories && (
                  <div className="pt-2 border-t border-[#F2F0EB]">
                    <button
                      onClick={() => setExpandedItemId(expandedItemId === item.id ? null : item.id)}
                      className="flex items-center justify-between w-full text-[10px] font-[800] uppercase tracking-[0.5px] text-[#06539B] hover:text-[#14457E]"
                    >
                      <span>
                        {t('selections.options.title')}
                        {item.options && item.options.length > 0 && (
                          <span className="ml-1.5 text-[#9E9890]">({item.options.length})</span>
                        )}
                      </span>
                      {expandedItemId === item.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>

                    {expandedItemId === item.id && (
                      <div className="mt-2 space-y-2">
                        {(!item.options || item.options.length === 0) && (
                          <div className="text-[10px] font-[600] text-[#9E9890] italic">
                            {t('selections.options.noOptions')}
                          </div>
                        )}
                        {item.options?.map(opt => {
                          const delta = buildDeltaPill(opt.price, item.categoryAllowance, t)
                          return (
                            <div
                              key={opt.id}
                              className={`bg-white border rounded-xl p-2.5 ${
                                opt.isApprovedByClient ? 'border-green-300 ring-1 ring-green-200' : 'border-[#ECEAE4]'
                              }`}
                            >
                              <div className="flex gap-2">
                                {opt.imageUrl ? (
                                  <img
                                    src={opt.imageUrl}
                                    alt={opt.name}
                                    className="w-12 h-12 rounded-lg object-cover bg-[#F2F0EB] flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-lg bg-[#F2F0EB] flex items-center justify-center flex-shrink-0">
                                    <Package size={18} className="text-[#CECAC4]" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-1.5">
                                    <div className="text-[11px] font-[800] text-[#1C1A17] truncate">{opt.name}</div>
                                    {opt.isApprovedByClient && (
                                      <span className="text-[8px] font-[800] uppercase tracking-[0.5px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                        {t('selections.options.status.approved')}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[10px] font-[700] text-[#9E9890] mt-0.5 truncate">
                                    {opt.brand && <span>{opt.brand}</span>}
                                    {opt.brand && opt.modelNumber && <span> · </span>}
                                    {opt.modelNumber && <span>{opt.modelNumber}</span>}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <span className="text-[12px] font-[900] text-[#1C1A17]">${opt.price.toLocaleString()}</span>
                                    {delta && (
                                      <span className="text-[10px] font-[800]" style={{ color: delta.color }}>
                                        {delta.label}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-1.5 mt-2">
                                <button
                                  onClick={() => setOptionModal({
                                    itemId: item.id,
                                    editingId: opt.id,
                                    form: {
                                      name: opt.name,
                                      description: opt.description || '',
                                      price: opt.price,
                                      brand: opt.brand || '',
                                      modelNumber: opt.modelNumber || '',
                                      imageUrl: opt.imageUrl || '',
                                      externalUrl: opt.externalUrl || '',
                                      supplierId: opt.supplierId || '',
                                    },
                                  })}
                                  className="flex items-center gap-1 text-[10px] font-[800] text-[#06539B] hover:bg-[#EEF2FF] px-2 py-1 rounded-full"
                                >
                                  <Edit3 size={10} /> {t('selections.options.actions.edit')}
                                </button>
                                <button
                                  onClick={() => {
                                    if (isDemo) return
                                    if (window.confirm(t('selections.options.confirmDelete'))) {
                                      optionDelete.mutate(opt.id)
                                    }
                                  }}
                                  className="flex items-center gap-1 text-[10px] font-[800] text-red-500 hover:bg-red-50 px-2 py-1 rounded-full"
                                >
                                  <Trash2 size={10} /> {t('selections.options.actions.delete')}
                                </button>
                                {!opt.isApprovedByClient && (
                                  <button
                                    onClick={() => { if (isDemo) return; optionApprove.mutate(opt.id) }}
                                    className="flex items-center gap-1 text-[10px] font-[800] text-white bg-green-600 hover:bg-green-700 px-2.5 py-1 rounded-full ml-auto"
                                  >
                                    <Check size={10} /> {t('selections.options.actions.approve')}
                                  </button>
                                )}
                              </div>
                            </div>
                          )
                        })}
                        <button
                          onClick={() => setOptionModal({ itemId: item.id, editingId: null, form: EMPTY_OPTION_FORM })}
                          className="w-full flex items-center justify-center gap-1.5 border border-dashed border-[#CFD8E3] rounded-xl py-2 text-[11px] font-[800] text-[#06539B] hover:bg-[#EEF2FF] transition-colors"
                        >
                          <Plus size={12} /> {t('selections.options.addOption')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Option modal ─────────────────────────── */}
      {/* PR #138B: option modal cannot open in demo (entry buttons
          are gated). The !isDemo wrapper here is defense-in-depth. */}
      {!isDemo && optionModal && (
        <OptionFormModal
          state={optionModal}
          isEs={isEs}
          t={t}
          onClose={() => setOptionModal(null)}
          onSave={(form) => {
            if (isDemo) return
            if (optionModal.editingId) {
              optionUpdate.mutate({ optionId: optionModal.editingId, data: form })
            } else {
              optionCreate.mutate({ itemId: optionModal.itemId, data: form })
            }
          }}
          isSaving={optionCreate.isPending || optionUpdate.isPending}
        />
      )}

      {/* ── Import template modal ─────────────────── */}
      {!isDemo && showImportModal && (
        <ImportTemplateModal
          isEs={isEs}
          t={t}
          getToken={getToken}
          onClose={() => setShowImportModal(false)}
          onApply={(templateId) => { if (isDemo) return; applyTemplate.mutate(templateId) }}
          isApplying={applyTemplate.isPending}
        />
      )}

      {/* ── Template library modal — full CRUD; on close, refresh the
          import-modal's template list so newly-created templates show up
          immediately if the user opens Import next. */}
      <TemplateLibraryModal
        isOpen={showTemplateLibrary}
        onClose={() => {
          setShowTemplateLibrary(false)
          queryClient.invalidateQueries({ queryKey: ['selection-templates'] })
        }}
      />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// OPTION FORM MODAL — full-screen on mobile, centered on desktop.
// imageUrl/externalUrl accept pasted URLs from supplier catalogs.
// File-upload handoff to PhotosModule storage is deferred (see commit notes).
// ─────────────────────────────────────────────────────────────────────

interface OptionFormModalProps {
  state: { itemId: string; editingId: string | null; form: OptionFormData }
  isEs: boolean
  t: (k: string, opts?: Record<string, unknown>) => string
  onClose: () => void
  onSave: (form: OptionFormData) => void
  isSaving: boolean
}

function OptionFormModal({ state, t, onClose, onSave, isSaving }: OptionFormModalProps) {
  const [form, setForm] = useState<OptionFormData>(state.form)
  const isEdit = !!state.editingId
  const canSave = form.name.trim().length > 0 && form.price >= 0

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#ECEAE4] px-5 py-3 flex items-center justify-between">
          <h3 className="text-[14px] font-[900] text-[#1C1A17]">
            {isEdit ? t('selections.options.actions.edit') : t('selections.options.addOption')}
          </h3>
          <button onClick={onClose} className="text-[#9E9890] hover:text-[#1C1A17]">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <Field label={t('selections.options.form.name')} required>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
          </Field>
          <Field label={t('selections.options.form.description')}>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
          </Field>
          <Field label={t('selections.options.form.price')} required>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] font-[700] text-[#9E9890]">$</span>
              <input
                type="number"
                step="0.01"
                value={form.price || ''}
                onChange={e => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                className="w-full border border-[#ECEAE4] rounded-lg pl-7 pr-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
              />
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label={t('selections.options.form.brand')}>
              <input
                type="text"
                value={form.brand}
                onChange={e => setForm({ ...form, brand: e.target.value })}
                className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
              />
            </Field>
            <Field label={t('selections.options.form.modelNumber')}>
              <input
                type="text"
                value={form.modelNumber}
                onChange={e => setForm({ ...form, modelNumber: e.target.value })}
                className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
              />
            </Field>
          </div>
          <Field label={t('selections.options.form.imageUrl')}>
            <input
              type="url"
              placeholder="https://..."
              value={form.imageUrl}
              onChange={e => setForm({ ...form, imageUrl: e.target.value })}
              className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
          </Field>
          <Field label={t('selections.options.form.externalUrl')}>
            <input
              type="url"
              placeholder="https://..."
              value={form.externalUrl}
              onChange={e => setForm({ ...form, externalUrl: e.target.value })}
              className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
          </Field>
          <Field label={t('selections.options.form.supplier')}>
            <input
              type="text"
              value={form.supplierId}
              onChange={e => setForm({ ...form, supplierId: e.target.value })}
              className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600] focus:outline-none focus:border-[#06539B]"
            />
          </Field>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#ECEAE4] px-5 py-3 flex gap-2 justify-end">
          <button onClick={onClose} className="border border-[#ECEAE4] rounded-full px-4 py-2 text-[12px] font-[800] text-[#9E9890] hover:bg-[#F2F0EB]">
            {t('Cancel')}
          </button>
          <button
            onClick={() => onSave(form)}
            disabled={!canSave || isSaving}
            className="bg-[#06539B] hover:bg-[#14457E] text-white rounded-full px-5 py-2 text-[12px] font-[800] disabled:opacity-40 transition-colors"
          >
            {isSaving ? '…' : t('Save')}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </div>
      {children}
    </label>
  )
}

// ─────────────────────────────────────────────────────────────────────
// IMPORT TEMPLATE MODAL — pulls user templates and applies one to project.
// ─────────────────────────────────────────────────────────────────────

interface ImportTemplateRecord {
  id: string
  name: string
  description?: string | null
  categories: { id: string }[]
}

interface ImportTemplateModalProps {
  isEs: boolean
  t: (k: string, opts?: Record<string, unknown>) => string
  getToken: () => Promise<string | null>
  onClose: () => void
  onApply: (templateId: string) => void
  isApplying: boolean
}

function ImportTemplateModal({ isEs, t, getToken, onClose, onApply, isApplying }: ImportTemplateModalProps) {
  const { data: templates, isLoading, isError, refetch } = useQuery<ImportTemplateRecord[]>({
    queryKey: ['selection-templates-list'],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch('/api/selection-templates', {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('API unavailable')
      return res.json()
    },
    retry: false,
  })

  // Defensive: never assume the response is a well-formed array of templates
  // each with a `categories` array. A malformed / old-shape response must
  // surface an honest empty or error state — never throw during render and
  // trip the module ErrorBoundary ("Algo salió mal").
  const list = Array.isArray(templates) ? templates : []

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#ECEAE4] px-5 py-3 flex items-center justify-between">
          <h3 className="text-[14px] font-[900] text-[#1C1A17]">
            {t('selections.templates.import.modalTitle')}
          </h3>
          <button onClick={onClose} className="text-[#9E9890] hover:text-[#1C1A17]">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-2">
          {isLoading && (
            <div className="text-[12px] font-[600] text-[#9E9890] text-center py-6" data-testid="import-templates-loading">
              {isEs ? 'Cargando plantillas…' : 'Loading templates…'}
            </div>
          )}
          {!isLoading && isError && (
            <div className="text-center py-6" data-testid="import-templates-error">
              <p className="text-[12px] font-[600] text-[#9E9890]">
                {isEs
                  ? 'No se pudieron cargar las plantillas. Verifica tu conexión e inténtalo de nuevo.'
                  : 'Couldn’t load templates. Check your connection and try again.'}
              </p>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center gap-1.5 mt-3 bg-[#06539B] hover:bg-[#14457E] text-white text-[12px] font-[800] px-4 py-2 rounded-full transition-colors"
              >
                {isEs ? 'Reintentar' : 'Try again'}
              </button>
            </div>
          )}
          {!isLoading && !isError && list.length === 0 && (
            <div className="text-[12px] font-[600] text-[#9E9890] text-center py-6" data-testid="import-templates-empty">
              {t('selections.templates.noTemplates')}
            </div>
          )}
          {!isLoading && !isError && list.map(tpl => (
            <div key={tpl.id} className="bg-white border border-[#ECEAE4] rounded-xl p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[13px] font-[800] text-[#1C1A17] truncate">{localizeTemplateName(tpl.name, isEs)}</div>
                {localizeTemplateDescription(tpl.name, tpl.description, isEs) && (
                  <div className="text-[11px] font-[600] text-[#9E9890] truncate">{localizeTemplateDescription(tpl.name, tpl.description, isEs)}</div>
                )}
                <div className="text-[10px] font-[700] text-[#06539B] mt-0.5">
                  {t('selections.templates.import.categoriesCount', { count: tpl.categories?.length ?? 0 })}
                </div>
              </div>
              <button
                onClick={() => onApply(tpl.id)}
                disabled={isApplying}
                className="bg-[#06539B] hover:bg-[#14457E] text-white text-[12px] font-[800] px-4 py-2 rounded-full disabled:opacity-40 transition-colors whitespace-nowrap"
              >
                {t('selections.templates.import.applyButton')}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
